import { getIn, useFormikContext } from 'formik';
import { useId } from 'react';
import { TotalVolumeCapOperator } from 'src/modules/conversions/domain';
import { TotalVolumeCapOperatorLabels } from 'src/modules/payout-term/components/stacks/Advanced_Settings/PayoutFilter';

import { Callout } from '@/components/callouts/Callout';
import { FormikInput, FormikSelect } from '@/components/forms';
import { Collapsible, CollapsibleContent, SelectItem, Switch, Text } from '@/design-system/components/ui';

import { UtilizationThresholdFormValues } from '../../../domain';

interface Props {
  unrepresentableExpression?: string;
}

export const UtilizationThreshold = ({ unrepresentableExpression }: Props) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<UtilizationThresholdFormValues>();
  const labelId = useId();
  const descriptionId = useId();

  const isEnabled = Boolean(values.utilizationThresholdEnabled);
  const hasError = Boolean(
    (getIn(touched, 'utilizationPercentage') && getIn(errors, 'utilizationPercentage')) ||
    (getIn(touched, 'utilizationOperator') && getIn(errors, 'utilizationOperator')),
  );

  const handleToggle = (checked: boolean) => {
    setFieldValue('utilizationThresholdEnabled', checked);
    setFieldValue('utilizationOperator', checked ? TotalVolumeCapOperator.GREATER_OR_EQUAL : null);
    if (!checked) {
      setFieldValue('utilizationPercentage', null);
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:flex-row tw:justify-between tw:items-center">
        <Text id={labelId} variant="body-md" weight="medium">
          Utilization threshold
        </Text>
        <Switch
          aria-labelledby={labelId}
          aria-describedby={descriptionId}
          checked={isEnabled}
          disabled={Boolean(unrepresentableExpression)}
          onCheckedChange={handleToggle}
        />
      </div>

      <Text id={descriptionId} variant="body-sm" color="muted">
        Utilization is the share of the reserve that is borrowed. The condition is checked on every daily snapshot.
        Snapshots that don&apos;t meet it earn no rewards.
      </Text>

      {unrepresentableExpression && (
        <Callout
          type="warning"
          title="This trigger has a condition that can't be edited here"
          body={`${unrepresentableExpression} — it is kept as it is when you save.`}
        />
      )}

      <Collapsible open={isEnabled}>
        <CollapsibleContent className="tw:mt-1">
          <div className="tw:flex tw:flex-col">
            <div className="tw:flex tw:flex-row tw:items-center tw:gap-2 tw:cursor-default">
              <Text variant="body-md" color={hasError ? 'error' : 'muted'} as="span">
                Only reward snapshots with utilization
              </Text>
              <FormikSelect name="utilizationOperator" placeholder="Select operator">
                {Object.entries(TotalVolumeCapOperatorLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    <Text variant="body-md" as="span">
                      {label}
                    </Text>
                  </SelectItem>
                ))}
              </FormikSelect>
              <FormikInput
                name="utilizationPercentage"
                type="number"
                min={0}
                max={100}
                step="any"
                placeholder="80"
                aria-label="Utilization percentage"
                className="tw:w-[80px] tw:[&_input]:text-right"
              />
              <span className={hasError ? 'tw:text-destructive' : ''}>
                <Text variant="body-md" color="muted" as="span">
                  %
                </Text>
              </span>
            </div>
            {getIn(touched, 'utilizationPercentage') && getIn(errors, 'utilizationPercentage') && (
              <p className="tw:text-sm tw:text-destructive">{getIn(errors, 'utilizationPercentage')}</p>
            )}
            {getIn(touched, 'utilizationOperator') && getIn(errors, 'utilizationOperator') && (
              <p className="tw:text-sm tw:text-destructive">{getIn(errors, 'utilizationOperator')}</p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
